import axios from 'axios'
import { USER_LOGOUT } from '../../admin_module/constants/userConstants'
import {
  ASSIGN_PARTI_FAIL,
  ASSIGN_PARTI_REQUEST,
  ASSIGN_PARTI_SUCCESS,
  ALL_PARTI_LIST_FAIL,
  ALL_PARTI_LIST_REQUEST,
  ALL_PARTI_LIST_RESET,
  ALL_PARTI_LIST_SUCCESS,
  PARTI_LIST_FAIL,
  PARTI_LIST_REQUEST,
  PARTI_LIST_RESET,
  PARTI_LIST_RELOAD_TARGET_PARTICIPANT,
  PARTI_LIST_SUCCESS,
  DOWNLOAD_TEXT_RESP_FAIL,
  DOWNLOAD_TEXT_RESP_REQUEST,
  DOWNLOAD_TEXT_RESP_SUCCESS,
  DOWNLOAD_IMG_RESP_REQUEST,
  DOWNLOAD_IMG_RESP_SUCCESS,
  DOWNLOAD_IMG_RESP_FAIL,
  DOWNLOAD_RESP_TIME_REQUEST,
  DOWNLOAD_RESP_TIME_SUCCESS,
  DOWNLOAD_RESP_TIME_FAIL,
  CREATE_PARTI_REQUEST,
  CREATE_PARTI_SUCCESS,
  CREATE_PARTI_FAIL,
} from '../constants/participantConstants'

import BACKEND_URL from '../../backendUrl'

const standardErrorHandling = (dispatch, error, errorConstant) => {
  if (error.response && error.response.status === 401) {
    // This ensure userInfo is clear
    dispatch({
      type: USER_LOGOUT,
    })
    localStorage.removeItem('adminModuleUserInfo')
  } else {
    // In the custom express error handler, there could be custom error message,
    //  which is stored in error.response as { message: ..., stack: ... }
    // If there is no such custom error message, output the generic error message (i.e. error.message)
    dispatch({
      type: errorConstant,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
  }
}

const pollExportJobUntilComplete = async (
  jobId,
  authHeaders,
  { joinedExisting = false } = {}
) => {
  const pollConfig = {
    timeout: 30000,
    headers: authHeaders,
  }

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
  const maxAttempts = 1300

  for (let attempt = 0; attempt < maxAttempts; ++attempt) {
    const { data: jobData } = await axios.get(
      BACKEND_URL + `/api/data/export_jobs/${jobId}`,
      pollConfig
    )

    if (jobData.status === 'completed') {
      if (jobData.downloadUrl) {
        const a = document.createElement('A')
        a.href = jobData.downloadUrl
        a.download = jobData.title || 'text_output.zip'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        try {
          await axios.post(
            BACKEND_URL + `/api/data/export_jobs/${jobId}/mark_downloaded`,
            {},
            { headers: authHeaders }
          )
        } catch (markError) {
          // Download already triggered; marking is best-effort for banner cleanup.
          console.warn('Failed to mark export downloaded', markError)
        }

        return {
          message: joinedExisting
            ? 'Export downloaded successfully (joined an export already in progress)'
            : 'Export downloaded successfully',
        }
      }

      if (jobData.downloadExpired) {
        throw new Error(
          'Export download window has expired. Please start a new export.'
        )
      }
    }

    if (jobData.status === 'failed') {
      throw new Error(jobData.error || 'Export failed')
    }

    await sleep(3000)
  }

  throw new Error(
    'Export is taking longer than expected. Please try again later.'
  )
}

export const fetchMyActiveExportJob = (studyId) => async (dispatch, getState) => {
  const {
    userLogin: { userInfo },
  } = getState()

  const config = {
    headers: {
      Authorization: `Bearer ${userInfo.token}`,
    },
    params: { studyId },
  }

  const { data } = await axios.get(
    BACKEND_URL + '/api/data/export_jobs/mine/active',
    config
  )

  return data.job
}

export const resumeExportJobPoll = (jobId) => async (dispatch, getState) => {
  try {
    dispatch({
      type: DOWNLOAD_TEXT_RESP_REQUEST,
    })

    const {
      userLogin: { userInfo },
    } = getState()

    const authHeaders = {
      Authorization: `Bearer ${userInfo.token}`,
    }

    const result = await pollExportJobUntilComplete(jobId, authHeaders, {
      joinedExisting: true,
    })

    dispatch({
      type: DOWNLOAD_TEXT_RESP_SUCCESS,
      payload: result,
    })

    return { success: true }
  } catch (error) {
    standardErrorHandling(dispatch, error, DOWNLOAD_TEXT_RESP_FAIL)
    return { success: false }
  }
}

export const getAllPartiList = (studyId) => async (dispatch, getState) => {
  try {
    dispatch({ type: ALL_PARTI_LIST_REQUEST })

    // getState() will return the whole redux state.
    // Check store.js combineReducer to see the key of the target state.
    const {
      userLogin: { userInfo },
    } = getState()

    //Following demostrate how to create data with axios in front-end
    //Authorization does not need to be quoted
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.get(
      BACKEND_URL + `/api/participants/${studyId}/get_participants`,
      config
    )

    dispatch({
      type: ALL_PARTI_LIST_SUCCESS,
      payload: data,
    })
  } catch (error) {
    standardErrorHandling(dispatch, error, ALL_PARTI_LIST_FAIL)
  }
}

// currentStage: id of current stage
// currentGroup: either is 'all' representing all participant groups or id of current participant group
// participantId: if specified, only get that participant data for single participant reload
// payload data => { participants:[...] }
export const getPartiList =
  (studyId, currentStage, currentGroup, participantId) =>
  async (dispatch, getState) => {
    try {
      // For keeping other participants data while reloading only one participant
      const {
        partiList: { participantsData },
      } = getState()
      const existingParticipants = participantsData.participants

      dispatch({ type: PARTI_LIST_REQUEST })

      // getState() will return the whole redux state.
      // Check store.js combineReducer to see the key of the target state.
      const {
        userLogin: { userInfo },
      } = getState()

      //Following demostrate how to create data with axios in front-end
      //Authorization does not need to be quoted
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      let postBody = {}
      postBody.currentStageId = currentStage
      if (currentGroup !== 'all') {
        postBody.currentGroupId = currentGroup
      }
      if (participantId) {
        postBody.participantId = participantId
        // Also remove the participant data with participantId in existing participantsData
        const indexOfParticipantToBeRemoved = existingParticipants.findIndex(
          (x) => String(x._id) === String(participantId)
        )
        if (indexOfParticipantToBeRemoved !== -1) {
          existingParticipants.splice(indexOfParticipantToBeRemoved, 1)
        }
      }

      let { data } = await axios.post(
        BACKEND_URL + `/api/participants/${studyId}/get_filtered_participants`,
        postBody,
        config
      )

      // If particpantId is specified, add the updated participantData back to participantsData list
      if (participantId) {
        if (data.participants.length > 0) {
          existingParticipants.push(data.participants[0])
        }
        data = { participants: existingParticipants }
      }

      dispatch({
        type: PARTI_LIST_SUCCESS,
        payload: data,
      })
    } catch (error) {
      standardErrorHandling(dispatch, error, PARTI_LIST_FAIL)
    }
  }

export const assignParticipant =
  (enrolId, groupInfo) => async (dispatch, getState) => {
    try {
      dispatch({
        type: ASSIGN_PARTI_REQUEST,
      })

      // getState() will return the whole redux state.
      // Check store.js combineReducer to see the key of the target state.
      const {
        userLogin: { userInfo },
      } = getState()

      //Following demostrate how to create data with axios in front-end
      //Authorization does not need to be quoted
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.patch(
        BACKEND_URL + `/api/participants/${enrolId}/assign_group`,
        groupInfo,
        config
      )

      dispatch({
        type: ASSIGN_PARTI_SUCCESS,
        payload: data,
      })
      // Trigger reload of participant data
      dispatch({
        type: PARTI_LIST_RELOAD_TARGET_PARTICIPANT,
      })
      dispatch({
        type: ALL_PARTI_LIST_RESET,
      })
    } catch (error) {
      standardErrorHandling(dispatch, error, ASSIGN_PARTI_FAIL)
    }
  }

export const downloadTextResp =
  (studyId, stage, group, excludedParticipantsIds, removeDuplicate) =>
  async (dispatch, getState) => {
    try {
      dispatch({
        type: DOWNLOAD_TEXT_RESP_REQUEST,
      })

      const {
        userLogin: { userInfo },
      } = getState()

      const authHeaders = {
        Authorization: `Bearer ${userInfo.token}`,
      }

      let postBody = {}
      postBody.stageId = stage
      if (group !== 'all') {
        postBody.groupId = group
      }
      postBody.removeDuplicate = removeDuplicate
      postBody.excludedParticipantsIds = excludedParticipantsIds

      const startResponse = await axios.post(
        BACKEND_URL + `/api/data/${studyId}/download_text_resp`,
        postBody,
        {
          timeout: 60000,
          headers: authHeaders,
          validateStatus: (status) => status === 202 || status === 409,
        }
      )

      const startData = startResponse.data
      const jobId = startData.jobId

      if (!jobId) {
        throw new Error('Export could not be started')
      }

      if (startData.userOwnActiveJob) {
        dispatch({
          type: DOWNLOAD_TEXT_RESP_FAIL,
          payload: startData.message,
        })
        return { userOwnActiveJob: true, job: startData.job }
      }

      const result = await pollExportJobUntilComplete(jobId, authHeaders, {
        joinedExisting: startData.alreadyInProgress,
      })

      dispatch({
        type: DOWNLOAD_TEXT_RESP_SUCCESS,
        payload: result,
      })

      return { success: true }
    } catch (error) {
      standardErrorHandling(dispatch, error, DOWNLOAD_TEXT_RESP_FAIL)
      return { success: false }
    }
  }

export const downloadRespTime =
  (studyId, stage, group) => async (dispatch, getState) => {
    try {
      dispatch({
        type: DOWNLOAD_RESP_TIME_REQUEST,
      })

      // getState() will return the whole redux state.
      // Check store.js combineReducer to see the key of the target state.
      const {
        userLogin: { userInfo },
      } = getState()

      //Following demostrate how to create data with axios in front-end
      //Authorization does not need to be quoted
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      let postBody = {}
      postBody.stageId = stage
      if (group !== 'all') {
        postBody.groupId = group
      }

      const { data } = await axios.post(
        BACKEND_URL + `/api/data/${studyId}/download_resp_time`,
        postBody,
        config
      )

      let file_path =
        'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
        data.content
      let a = document.createElement('A')
      a.href = file_path
      a.download = data.title
      document.body.appendChild(a)
      setTimeout(() => {
        a.click()
        document.body.removeChild(a)

        dispatch({
          type: DOWNLOAD_RESP_TIME_SUCCESS,
          payload: data,
        })
      }, 1000)
    } catch (error) {
      standardErrorHandling(dispatch, error, DOWNLOAD_RESP_TIME_FAIL)
    }
  }

export const createParticipant =
  (studyId, email, password) => async (dispatch, getState) => {
    try {
      dispatch({
        type: CREATE_PARTI_REQUEST,
      })

      // getState() will return the whole redux state.
      // Check store.js combineReducer to see the key of the target state.
      const {
        userLogin: { userInfo },
      } = getState()

      //Following demostrate how to create data with axios in front-end
      //Authorization does not need to be quoted
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.post(
        BACKEND_URL + `/api/participants/${studyId}/create`,
        { studyId, email, password },
        config
      )

      dispatch({
        type: CREATE_PARTI_SUCCESS,
        payload: data,
      })
      // Trigger reload of participant data
      dispatch({
        type: PARTI_LIST_RESET,
      })
      dispatch({
        type: ALL_PARTI_LIST_RESET,
      })
    } catch (error) {
      standardErrorHandling(dispatch, error, CREATE_PARTI_FAIL)
    }
  }
